/**
 * Utilitários de formatação centralizados
 */

/**
 * Formata valor monetário para exibição em Real (R$)
 */
export const formatCurrency = (value: number): string => {
 // Validar se o valor é um número válido
 const numValue =
 typeof value === "number" ? value : parseFloat(String(value)) || 0;
 if (isNaN(numValue) || !isFinite(numValue)) {
 return "R$ 0,00";
 }
 return new Intl.NumberFormat("pt-BR", {
 style: "currency",
 currency: "BRL",
 minimumFractionDigits: 2,
 maximumFractionDigits: 2,
 }).format(numValue);
};

/**
 * Formata string de preço (input do usuário) para formato monetário
 */
export const formatPriceInput = (value: string): string => {
 const numbers = value.replace(/\D/g, "");
 if (numbers.length === 0) return "";

 const numberValue = parseInt(numbers);
 return formatCurrency(numberValue / 100);
};

/**
 * Converte string de preço formatado para número
 */
export const parseCurrency = (value: string): number => {
 const numbers = value.replace(/\D/g, "");
 return parseInt(numbers || "0") / 100;
};

export const numberToCurrencyInputValue = (value: number): string => {
 if (!Number.isFinite(value) || value < 0) return "";
 const cents = Math.round(value * 100);
 return formatPriceInput(String(cents));
};

/**
 * Formata quilometragem com separador de milhares
 */
export const formatMileage = (value: string | number): string => {
 const numbers = value.toString().replace(/\D/g, "");
 return numbers.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
};

/**
 * Converte quilometragem formatada para número
 */
export const parseMileage = (value: string): number => {
 return parseInt(value.replace(/\D/g, "") || "0");
};

/**
 * Formata placa de veículo (suporta formatos antigo e Mercosul)
 */
export const formatPlate = (value: string): string => {
 const clean = value.replace(/[^A-Za-z0-9]/g, "").toUpperCase().substring(0, 7);
 const prefix = clean.substring(0, 3).replace(/[^A-Z]/g, "");
 const tail = clean.substring(3);

 if (prefix.length < 3) {
  return prefix;
 }

 if (tail.length === 0) {
  return prefix;
 }

 const firstTail = tail.charAt(0);
 const secondTail = tail.charAt(1);
 const isMercosul =
  /[0-9]/.test(firstTail) &&
  (tail.length === 1 || (tail.length >= 2 && /[A-Z]/.test(secondTail)));

 if (isMercosul) {
  const d1 = tail.substring(0, 1).replace(/[^0-9]/g, "");
  const l1 = tail.substring(1, 2).replace(/[^A-Z]/g, "");
  const d2 = tail.substring(2, 4).replace(/[^0-9]/g, "");
  return `${prefix}${d1}${l1}${d2}`;
 }

 const digits = tail.replace(/[^0-9]/g, "").substring(0, 4);
 return digits.length > 0 ? `${prefix}-${digits}` : prefix;
};

/**
 * Formata chassi (17 caracteres alfanuméricos)
 */
export const formatChassis = (value: string): string => {
 return value
 .replace(/[^A-Za-z0-9]/g, "")
 .toUpperCase()
 .substring(0, 17);
};

/**
 * Formata RENAVAM (11 dígitos numéricos)
 */
export const formatRenavam = (value: string): string => {
 return value.replace(/\D/g, "").substring(0, 11);
};

/**
 * Formata ano (apenas 4 dígitos)
 */
export const formatYear = (value: string): string => {
 return value.replace(/\D/g, "").substring(0, 4);
};

/**
 * Formata percentual
 */
export const formatPercentage = (value: number): string => {
 return new Intl.NumberFormat("pt-BR", {
 style: "percent",
 minimumFractionDigits: 1,
 maximumFractionDigits: 1,
 }).format(value / 100);
};

/**
 * Formata percentual para input (ex: 10.5 -> "10,5%")
 */
export const formatPercentageInput = (value: number): string => {
 if (isNaN(value) || value === 0) return "";
 return (
 new Intl.NumberFormat("pt-BR", {
 minimumFractionDigits: 1,
 maximumFractionDigits: 2,
 }).format(value) + "%"
 );
};

/**
 * Converte string de percentual formatado para número
 * Ex: "10,5%" -> 10.5
 */
export const parsePercentage = (value: string): number => {
 // Remover % e caracteres não numéricos exceto vírgula e ponto
 const cleanValue = value.replace(/[^\d,.-]/g, "").replace(",", ".");
 const parsed = parseFloat(cleanValue) || 0;
 return isNaN(parsed) ? 0 : parsed;
};

/**
 * Formata número com separador de milhares
 */
export const formatNumber = (value: number): string => {
 return new Intl.NumberFormat("pt-BR").format(value);
};

/**
 * Converte data do formato ISO (yyyy-mm-dd) para formato brasileiro (dd/mm/yyyy)
 */
export const formatDateToBR = (dateString: string): string => {
 if (!dateString) return "";
 const date = new Date(dateString + "T00:00:00");
 if (isNaN(date.getTime())) return "";

 const day = String(date.getDate()).padStart(2, "0");
 const month = String(date.getMonth() + 1).padStart(2, "0");
 const year = date.getFullYear();

 return `${day}/${month}/${year}`;
};

/**
 * Converte data do formato brasileiro (dd/mm/yyyy) para formato ISO (yyyy-mm-dd)
 */
export const parseDateFromBR = (dateString: string): string => {
 if (!dateString) return "";

 // Remove caracteres não numéricos exceto barras
 const cleanDate = dateString.replace(/[^\d/]/g, "");
 const parts = cleanDate.split("/");

 if (parts.length !== 3) return "";

 const day = parts[0].padStart(2, "0");
 const month = parts[1].padStart(2, "0");
 const year = parts[2];

 // Validar se é uma data válida
 if (day.length !== 2 || month.length !== 2 || year.length !== 4) return "";

 const date = new Date(`${year}-${month}-${day}T00:00:00`);
 if (isNaN(date.getTime())) return "";

 return `${year}-${month}-${day}`;
};

/**
 * Formata input de data enquanto o usuário digita (dd/mm/yyyy)
 */
export const formatDateInput = (value: string): string => {
 // Remove tudo exceto números
 const numbers = value.replace(/\D/g, "");

 if (numbers.length === 0) return "";
 if (numbers.length <= 2) return numbers;
 if (numbers.length <= 4) return `${numbers.slice(0, 2)}/${numbers.slice(2)}`;
 return `${numbers.slice(0, 2)}/${numbers.slice(2, 4)}/${numbers.slice(4, 8)}`;
};

export const localDateTimeInputToIso8601 = (value: string): string | undefined => {
  const t = value.trim();
  if (!t) return undefined;
  const d = new Date(t);
  if (Number.isNaN(d.getTime())) return undefined;
  return d.toISOString();
};

/**
 * Formata número de telefone WhatsApp para exibição
 * Ex: "5511999999999" -> "+55 11 99999-9999"
 */
export const formatPhoneNumber = (phoneNumber: string): string => {
  if (!phoneNumber) return '';

  // Remove tudo exceto números
  const numbers = phoneNumber.replace(/\D/g, '');

  // Se tiver código do país (55)
  if (numbers.length === 13 && numbers.startsWith('55')) {
    const countryCode = numbers.slice(0, 2);
    const areaCode = numbers.slice(2, 4);
    const firstPart = numbers.slice(4, 9);
    const secondPart = numbers.slice(9, 13);
    return `+${countryCode} ${areaCode} ${firstPart}-${secondPart}`;
  }

  // Se tiver apenas DDD + número (11 dígitos)
  if (numbers.length === 11) {
    const areaCode = numbers.slice(0, 2);
    const firstPart = numbers.slice(2, 7);
    const secondPart = numbers.slice(7, 11);
    return `${areaCode} ${firstPart}-${secondPart}`;
  }

  // Retorna original se não conseguir formatar
  return phoneNumber;
};
